import { Component, OnInit, ViewChild, ElementRef, QueryList, ViewChildren } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, AbstractControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { finalize, take, filter } from 'rxjs/operators';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { BsModalService, BsModalRef, ModalOptions } from 'ngx-bootstrap/modal';
import * as _ from 'lodash';
import * as moment from 'moment';

import { SearchCriteria, Page, SearchResult, SearchResultContent } from './search.model';

import { AuthenticationService } from '@service/authentication.service';
import { BrowserService } from '@service/browser.service';
import { Logger } from '@service/logger.service';
import { SearchService } from './search.service';

import { ResendComponent } from './resend/resend.component';
import { SentHistoryComponent } from './sent-history/sent-history.component';

import { User } from '@model/authentication.model';
import { SentHistoryResult } from './sent-history/sent-history.model';

import { Role } from '../core/enum/role.enum';
import { Status, PartyTypeCode, SortableHeader, DocType } from './search.enum';

import { SortEvent, SortableDirective, Direction } from '../shared/directive/sortable.directive';

const log: Logger = new Logger('SearchComponent');

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss']
})
export class SearchComponent implements OnInit {
  searchForm: UntypedFormGroup;
  pageConfig: Page;
  searchCriteria: SearchCriteria;
  searchResult: SearchResult;
  lang: string;
  resendModalRef: BsModalRef;
  sentHistoryModalRef: BsModalRef;
  sortable: SortEvent;

  sortableName: typeof SortableHeader = SortableHeader;
  partyTypeCode: typeof PartyTypeCode = PartyTypeCode;
  documentType: typeof DocType = DocType;

  loading = false;
  error = false;
  isUserCanPreviewAndResend: boolean;

  private currentUser: User;

  @ViewChild('searchResultSection') searchResultSection: ElementRef;

  @ViewChildren(SortableDirective) tableHeaders: QueryList<SortableDirective>;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private searchService: SearchService,
    private translationService: TranslateService,
    private browserService: BrowserService,
    private modalService: BsModalService,
    private authenticationService: AuthenticationService
  ) {}

  ngOnInit(): void {
    this.pageConfig = this.getDefaultPageConfig();
    this.searchForm = this.getDefaultForm();
    this.translationService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.lang = event.lang;
    });
    this.currentUser = this.authenticationService.currentUser;
    this.isUserCanPreviewAndResend = this.checkUserCanPreviewAndResend();
  }

  get partyType(): AbstractControl {
    return this.searchForm.get('partyType');
  }

  get idCardNo(): AbstractControl {
    return this.searchForm.get('idCardNo');
  }

  get policyNo(): AbstractControl {
    return this.searchForm.get('policyNo');
  }

  get firstName(): AbstractControl {
    return this.searchForm.get('firstName');
  }

  get lastName(): AbstractControl {
    return this.searchForm.get('lastName');
  }

  get docType(): AbstractControl {
    return this.searchForm.get('docType');
  }

  isDateExpired(expiredDate: string): boolean {
    return moment(expiredDate) < moment().startOf('day');
  }

  isFail(status: string): boolean {
    return status === Status.FAIL || status === Status.CRITICAL || status === Status.INQUEUE;
  }

  isNotstart(status: string): boolean {
    return status === Status.NOT_START;
  }

  clear(): void {
    this.searchForm = this.getDefaultForm();
  }

  pageChanged(event: number): void {
    this.loading = true;
    this.pageConfig.currentPage = event;
    this.search(this.searchCriteria, this.pageConfig.currentPage, this.pageConfig.itemsPerPage, false, this.sortable);
  }

  onSubmit(): void {
    this.loading = true;
    this.searchCriteria = {
      partyType: this.partyType.value,
      idCardNo: this.trim(this.idCardNo.value),
      policyNo: this.trim(this.policyNo.value),
      firstName: this.trim(this.firstName.value),
      lastName: this.trim(this.lastName.value),
      docType: this.trim(this.docType.value)
    } as SearchCriteria;

    this.pageConfig.currentPage = 1;
    this.resetSortHeader();
    this.search(this.searchCriteria, this.pageConfig.currentPage, this.pageConfig.itemsPerPage, true);
  }

  onSort(sortEvent?: SortEvent): void {
    this.resetSortHeader(sortEvent);

    this.search(this.searchCriteria, this.pageConfig.currentPage, this.pageConfig.itemsPerPage, false, this.sortable);
  }

  onClickResend(contentItem: SearchResultContent): void {
    if (contentItem.emailStatus === Status.NOT_START) {
      return;
    }
    this.openResendModal(contentItem);
  }

  onClickSentHistory(contentItem: SearchResultContent): void {
    this.loading = true;

    this.searchService
      .getSentHistory(contentItem.ePolicyId)
      .pipe(
        take(1),
        filter((sentHistoryData: SentHistoryResult) => sentHistoryData !== null),
        finalize(() => (this.loading = false))
      )
      .subscribe({
          next: (sentHistoryData: SentHistoryResult) => this.openSentHistoryModal(contentItem.contractNo, sentHistoryData),
          error: (error: Error) => {
            this.openSentHistoryModal();
            log.error('ERROR from senthistory', error);
          }
        });
  }

  preview(status: string, ePolicyId: string): void {
    if (status !== Status.SUCCESS) {
      return;
    }
    const spanElement: HTMLSpanElement = document.createElement('span');
    let ePolicyWindow: Window = null;
    this.loading = true;

    if (!this.browserService.isIEBrowser()) {
      ePolicyWindow = this.createLoadingWindow(spanElement);
    }

    this.searchService
      .downloadContract(ePolicyId)
      .pipe(
        take(1),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (data: BlobPart) => this.displayContractPdf(data, ePolicyWindow),
        error: (error: Error) => {
          const errorMessage = 'Something went wrong.';
          spanElement.textContent = errorMessage;
          log.error('ERROR from downloadContract()', error);
        }
      });
  }

  private getDefaultPageConfig(): Page {
    return {
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0
    };
  }

  private getDefaultForm(): UntypedFormGroup {
    return this.formBuilder.group({
      partyType: [this.partyTypeCode.INDIVIDUAL],
      firstName: [''],
      lastName: [''],
      idCardNo: [''],
      policyNo: [''],
      docType: ''
    });
  }

  private resetSortHeader(sortEvent?: SortEvent): void {
    this.sortable = sortEvent;
    if (sortEvent) {
      // resetting other headers
      this.tableHeaders.forEach((header: SortableDirective) => {
        if (header.sortBy !== this.sortable.column) {
          header.direction = '';
        }
      });
    } else {
      // set default headers
      this.tableHeaders.forEach((header: SortableDirective) => {
        if (header.sortBy === this.sortableName.LAST_SENT_DATE) {
          header.direction = Direction.ASC;
        } else {
          header.direction = '';
        }
      });
    }
  }

  private trim(value: string): string {
    if (value) {
      return value.trim();
    }
    return value;
  }

  private search(
    request: SearchCriteria,
    currentPage: number,
    itemsPerPage: number,
    requireAutoScroll: boolean,
    sortable?: SortEvent
  ): Subscription {
    return this.searchService
      .searchPolicy(request, currentPage, itemsPerPage, sortable)
      .pipe(
        take(1),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (data: SearchResult) => {
          this.error = false;
          this.searchResult = data;
          this.pageConfig.totalItems = data.totalElements;

          if (requireAutoScroll) {
            this.scrollToSearchResult();
          }

          if (!!data && !!data.content) {
            this.searchResult.content = data.content.map((content: SearchResultContent) =>
              this.mapDocumentTypeResult(content)
            );
          }
        },
        error: () => {
          this.error = true;
        }
      });
  }

  private mapDocumentTypeResult(content: SearchResultContent): SearchResultContent {
    if (content.docType === DocType.EPOLICY) {
      content.docType = 'Policy';
    } else if (content.docType === DocType.ERENEWALNOTICE) {
      content.docType = 'Renewal Notice';
    } else if (content.docType === DocType.PA_TAXDEDUCT) {
      content.docType = 'Tax Deduction';
    }
    return content;
  }

  private openResendModal(contentItem: SearchResultContent): void {
    const modalOptions: ModalOptions = {
      initialState: {
        ePolicyId: contentItem.ePolicyId,
        userLogin: this.currentUser.username,
        mails: contentItem.emailTo,
        mobileNumber: contentItem.lastSentMobileNo,
        mobileNumberWithoutEncode: contentItem.lastSentMobileNoWithoutEncode,
        productType: contentItem.productType,
        docType: contentItem.docType
      },
      ignoreBackdropClick: true,
      class: 'modal-custom'
    };

    this.resendModalRef = this.modalService.show(ResendComponent, modalOptions);
    this.resendModalRef.content.onResendClose.subscribe((success: boolean) => {
      if (success) {
        this.search(
          this.searchCriteria,
          this.pageConfig.currentPage,
          this.pageConfig.itemsPerPage,
          false,
          this.sortable
        );
      }
    });
  }

  private openSentHistoryModal(contractNo?: string, sentHistoryData?: SentHistoryResult): void {
    const modalOptions: ModalOptions = {
      initialState: {
        totalTimes: sentHistoryData ? sentHistoryData.totalTime : null,
        policyNo: contractNo,
        sendRecords: sentHistoryData ? sentHistoryData.ePolicyHistoryRecords : null
      },
      ignoreBackdropClick: true,
      class: 'modal-lg'
    };
    this.sentHistoryModalRef = this.modalService.show(SentHistoryComponent, modalOptions);
  }

  private createLoadingWindow(spanElement: HTMLSpanElement): Window {
    const ePolicyWindow: Window = window.open('about:blank', '_blank');
    const loadingMessage = 'Loading...';

    spanElement.textContent = loadingMessage;
    ePolicyWindow.document.body.appendChild(spanElement);

    return ePolicyWindow;
  }

  private displayContractPdf(contractPdf: BlobPart, ePolicyWindow: Window): void {
    if (this.browserService.isIEBrowser()) {
      const navigator: any = window.navigator;
      navigator.msSaveOrOpenBlob(contractPdf);
    } else {
      const contractBlob: Blob = new Blob([contractPdf], { type: 'application/pdf' });
      ePolicyWindow.location.href = URL.createObjectURL(contractBlob);
    }
  }

  private checkUserCanPreviewAndResend(): boolean {
    if (this.currentUser && this.currentUser.roles) {
      const index: number = _.findIndex(
        this.currentUser.roles,
        (role: string) => role === Role.CALLCENTER || role === Role.MARKETING
      );
      return index !== -1;
    }

    return false;
  }

  private scrollToSearchResult(): void {
    setTimeout(() => {
      this.searchResultSection.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }
}
